import { useMemo, useState, useCallback, useEffect } from "react";
import { money } from "@/lib/format";
import {
  type ProductOption,
  type CombinationRow,
  type VariantStatus,
  countCombinations,
  combinationSafety,
  generateCombinations,
  findDuplicateSkus,
  generateSkuFromPattern,
  validateSkuPattern,
  canonicalOptionSkuCode,
  uniqueSku,
} from "@/lib/products";
import { restoreVariationEditorFromPayload, type WizardCombinationPayload, type WizardVariationPayload } from "@/lib/wizard-draft";
import {
  manualKeysFromPayload,
  manualSkuValues,
  markManualSku,
  mergeRegeneratedCombinations,
  optionRemovalDecision,
  restoredSkuPattern,
  skuLockSummary,
  skuRewriteGuard,
  summarizeMatrixAuthoredData,
} from "@/lib/wizard-sku-lock";

const inputCls =
  "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";
const labelCls = "block text-xs font-medium text-slate-500 mb-1";

// Monotonic, deterministic ID for transient option rows (never persisted).
// Avoids Date.now() timestamp fragments and same-millisecond collisions.
let optionIdSeq = 2;

export interface VariationVariantPayload {
  sku: string;
  barcode: string | null;
  option_values: Record<string, string>;
  cost_price: number | null;
  selling_price: number | null;
  weight_kg: number | null;
  length_cm: number | null;
  width_cm: number | null;
  height_cm: number | null;
  opening_qty: number | null;
  position: number;
  status: string;
}

export interface VariationPayload {
  options: { name: string; position: number; values: { value: string; position: number }[] }[];
  variants: VariationVariantPayload[];
  /** Full editable matrix (including excluded rows). `variants` is the submission projection. */
  combinations?: WizardCombinationPayload[];
  combinationCount: number;
  duplicateSkus: string[];
  hasData: boolean;
  /** SKUs the operator typed by hand. Persisted so the locks survive a draft resume. */
  manualSkus?: string[];
  /** SKU pattern in use. Persisted so pattern and manual locks restore together. */
  skuPattern?: string;
}

interface Props {
  productSku: string | null;
  productName: string;
  defaultCost: number | null;
  defaultSelling: number | null;
  defaultWeightKg: number | null;
  defaultLengthCm: number | null;
  defaultWidthCm: number | null;
  defaultHeightCm: number | null;
  defaultLocationId?: string | null;
  initialPayload?: VariationPayload | WizardVariationPayload | null;
  onChange: (payload: VariationPayload) => void;
}

function parseValues(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  text.split(/[,;\n]/).forEach((raw) => {
    const v = raw.trim();
    if (v && !seen.has(v.toLowerCase())) {
      seen.add(v.toLowerCase());
      out.push(v);
    }
  });
  return out;
}

function tmpId(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) { h = ((h << 5) - h + seed.charCodeAt(i)) | 0; }
  return `tmp-${Math.abs(h).toString(36)}`;
}

export default function VariantMatrixEditor({
  productSku,
  productName,
  defaultCost,
  defaultSelling,
  defaultWeightKg,
  defaultLengthCm,
  defaultWidthCm,
  defaultHeightCm,
  defaultLocationId,
  initialPayload,
  onChange,
}: Props) {
  const restored = restoreVariationEditorFromPayload(initialPayload);
  const [options, setOptions] = useState<ProductOption[]>(
    restored?.options ?? [
      { id: "opt-0", org_id: "", product_id: "", name: "Colour", position: 0, status: "active", values: [] },
      { id: "opt-1", org_id: "", product_id: "", name: "Size", position: 1, status: "active", values: [] },
    ],
  );

  const [skuPattern, setSkuPattern] = useState(() =>
    restoredSkuPattern(initialPayload?.skuPattern) ?? (productSku
      ? `${productSku.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8)}-{COLOUR}-{SIZE}`
      : "{NAME}-{COLOUR}-{SIZE}"),
  );

  const [sharedCost, setSharedCost] = useState("");
  const [sharedSelling, setSharedSelling] = useState(defaultSelling != null ? String(defaultSelling) : "");
  const [sharedWeight, setSharedWeight] = useState(defaultWeightKg != null ? String(defaultWeightKg) : "");
  const [sharedLength, setSharedLength] = useState(defaultLengthCm != null ? String(defaultLengthCm) : "");
  const [sharedWidth, setSharedWidth] = useState(defaultWidthCm != null ? String(defaultWidthCm) : "");
  const [sharedHeight, setSharedHeight] = useState(defaultHeightCm != null ? String(defaultHeightCm) : "");

  const [combinations, setCombinations] = useState<CombinationRow[]>(restored?.combinations ?? []);
  const [generated, setGenerated] = useState((restored?.combinations.length ?? 0) > 0);
  const [manualSkuKeys, setManualSkuKeys] = useState<Set<string>>(
    () => manualKeysFromPayload(restored?.combinations ?? [], initialPayload?.manualSkus),
  );
  const [skuUnlocked, setSkuUnlocked] = useState(false);
  const [showDimOverrides, setShowDimOverrides] = useState(false);
  const [bulkQtyMode, setBulkQtyMode] = useState<"set" | "add" | "sub">("set");
  const [bulkQty, setBulkQty] = useState("");
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [prefix, setPrefix] = useState("");
  const [suffix, setSuffix] = useState("");
  const [page, setPage] = useState(0);
  const [bulkStatus, setBulkStatus] = useState<"active" | "draft" | "paused">("active");
  const [bulkDims, setBulkDims] = useState({ l: "", w: "", h: "" });

  const parsedOptions = useMemo(
    () => options.filter((o) => o.name.trim().length > 0 && o.values.length > 0),
    [options],
  );

  const comboCount = useMemo(() => countCombinations(parsedOptions), [parsedOptions]);
  const safety = useMemo(() => combinationSafety(parsedOptions), [parsedOptions]);

  const includedCount = useMemo(() => combinations.filter((c) => c.included).length, [combinations]);
  const dupSkus = useMemo(() => findDuplicateSkus(combinations, []), [combinations]);

  const buildPayload = useCallback((): VariationPayload => {
    const optionPayload = parsedOptions.map((o, pos) => ({
      name: o.name.trim(),
      position: pos,
      values: o.values.map((v, vPos) => ({ value: v.value, position: vPos })),
    }));

    const variants: VariationVariantPayload[] = combinations
      .filter((c) => c.included)
      .map((c, idx) => {
        const optionValues: Record<string, string> = {};
        for (let i = 0; i < parsedOptions.length && i < c.values.length; i++) {
          optionValues[parsedOptions[i].name.trim()] = c.values[i];
        }
        return {
          sku: c.sku,
          barcode: c.barcode,
          option_values: optionValues,
          cost_price: c.cost,
          selling_price: c.price,
          weight_kg: c.weightKg,
          length_cm: c.lengthCm,
          width_cm: c.widthCm,
          height_cm: c.heightCm,
          opening_qty: c.qty,
          position: idx,
          status: c.status,
        };
      });

    return {
      options: optionPayload,
      variants,
      combinations: combinations.map((c) => ({
        values: c.values,
        sku: c.sku,
        barcode: c.barcode,
        included: c.included,
        price: c.price,
        cost: c.cost,
        weight_kg: c.weightKg,
        length_cm: c.lengthCm,
        width_cm: c.widthCm,
        height_cm: c.heightCm,
        qty: c.qty ?? null,
        status: c.status,
      })),
      combinationCount: variants.length,
      duplicateSkus: dupSkus.map((d) => d.sku),
      hasData: variants.length > 0,
      manualSkus: manualSkuValues(combinations, manualSkuKeys),
      skuPattern: skuPattern.trim(),
    };
  }, [parsedOptions, combinations, dupSkus, manualSkuKeys, skuPattern]);

  useEffect(() => {
    onChange(buildPayload());
  }, [buildPayload, onChange]);

  const updateOptionName = (optId: string, name: string) => {
    setOptions((opts) => opts.map((o) => (o.id === optId ? { ...o, name } : o)));
    setGenerated(false);
  };

  // Raw per-option draft text so commas and spaces can be typed naturally.
  // Values are only parsed on blur/Enter — never on every keystroke (which used
  // to strip commas and collapse "Black, White, Blue" into one value).
  const [drafts, setDrafts] = useState<Record<string, string>>(() =>
    Object.fromEntries((restored?.options ?? []).map((option) => [option.id, option.values.map((value) => value.value).join(", ")])),
  );

  const draftValue = (o: ProductOption) => drafts[o.id] ?? o.values.map((v) => v.value).join(", ");

  const commitOptionValues = (optId: string, text: string) => {
    const parsed = parseValues(text);
    setOptions((opts) =>
      opts.map((o) =>
        o.id === optId
          ? { ...o, values: parsed.map((v, i) => ({ id: tmpId(`${o.name}:${v}`), org_id: "", product_option_id: optId, value: v, position: i, status: "active" as const })) }
          : o,
      ),
    );
    setDrafts((d) => ({ ...d, [optId]: parsed.join(", ") }));
    setGenerated(false);
  };

  const addOption = () => {
    setOptions((opts) => [
      ...opts,
      { id: `opt-${optionIdSeq++}`, org_id: "", product_id: "", name: "", position: opts.length, status: "active", values: [] },
    ]);
    setGenerated(false);
  };

  const removeOption = (optId: string) => {
    // Removing a dimension invalidates combinations. If the operator has
    // authored anything, say exactly what would be lost and require an
    // explicit confirmation. Cancel leaves the matrix byte-for-byte unchanged.
    const decision = optionRemovalDecision({
      summary: summarizeMatrixAuthoredData(combinations, manualSkuKeys),
      confirmed: false,
    });
    if (decision.needsConfirm && !window.confirm(decision.message)) return;
    setCombinations([]);
    setManualSkuKeys(new Set());
    setSkuUnlocked(false);
    setOptions((opts) => opts.filter((o) => o.id !== optId).map((o, i) => ({ ...o, position: i })));
    setDrafts((d) => { const nd = { ...d }; delete nd[optId]; return nd; });
    setGenerated(false);
  };

  const buildPreview = () => {
    setError("");
    const guard = skuRewriteGuard({
      action: "regenerate",
      manualCount: manualSkuKeys.size,
      unlocked: skuUnlocked,
      hasExistingMatrix: generated && combinations.length > 0,
      confirmed: false,
    });
    if (!guard.allowed) {
      if (guard.needsUnlock) { setError(guard.message ?? ""); return; }
      if (!window.confirm(guard.message ?? "")) return;
    }
    const patternErr = validateSkuPattern(skuPattern, parsedOptions.map((o) => o.name));
    if (patternErr) { setError(patternErr); return; }
    if (!safety.safe) { setError(safety.warning ?? "Too many combinations."); return; }
    const combos = generateCombinations(parsedOptions, skuPattern, { productSku, productName }, [], 0);
    const sCost = sharedCost.trim() === "" ? null : Number(sharedCost);
    const sSell = sharedSelling.trim() === "" ? null : Number(sharedSelling);
    const sW = sharedWeight.trim() === "" ? null : Number(sharedWeight);
    const sL = sharedLength.trim() === "" ? null : Number(sharedLength);
    const sWi = sharedWidth.trim() === "" ? null : Number(sharedWidth);
    const sH = sharedHeight.trim() === "" ? null : Number(sharedHeight);
    const withDefaults = combos.map((c) => ({ ...c, cost: sCost, price: sSell, weightKg: sW, lengthCm: sL, widthCm: sWi, heightCm: sH, qty: c.qty ?? null }));
    // A locked SKU survives the rebuild; unlocked SKUs come from the pattern.
    const merged = mergeRegeneratedCombinations({
      regenerated: withDefaults,
      previous: combinations,
      manualKeys: skuUnlocked ? new Set<string>() : manualSkuKeys,
    });
    setCombinations(merged);
    if (skuUnlocked) {
      setManualSkuKeys(new Set());
      setSkuUnlocked(false);
    }
    setGenerated(true);
  };

  const toggleIncluded = (idx: number) => setCombinations((cs) => cs.map((c, i) => (i === idx ? { ...c, included: !c.included } : c)));
  const setComboField = (idx: number, field: keyof CombinationRow, value: unknown) =>
    setCombinations((cs) => cs.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));

  const bulkSetPrice = (val: string) => { const n = val.trim() === "" ? null : Number(val); setCombinations((cs) => cs.map((c) => ({ ...c, price: n }))); };
  const bulkSetCost = (val: string) => { const n = val.trim() === "" ? null : Number(val); setCombinations((cs) => cs.map((c) => ({ ...c, cost: n }))); };
  const bulkSetWeight = (val: string) => { const n = val.trim() === "" ? null : Number(val); setCombinations((cs) => cs.map((c) => ({ ...c, weightKg: n }))); };
  const bulkSetStatus = (val: string) => setCombinations((cs) => cs.map((c) => ({ ...c, status: val as VariantStatus })));

  const applyToFiltered = (fn: (c: CombinationRow) => CombinationRow) => {
    const set = new Set(filteredCombinations);
    setCombinations((cs) => cs.map((c) => (set.has(c) ? fn(c) : c)));
  };
  const bulkActivate = () => applyToFiltered((c) => ({ ...c, included: true }));
  const bulkDeactivate = () => applyToFiltered((c) => ({ ...c, included: false }));

  const applyBulkDims = () => {
    const nl = bulkDims.l.trim() === "" ? null : Number(bulkDims.l);
    const nw = bulkDims.w.trim() === "" ? null : Number(bulkDims.w);
    const nh = bulkDims.h.trim() === "" ? null : Number(bulkDims.h);
    setCombinations((cs) => cs.map((c) => ({ ...c, lengthCm: nl, widthCm: nw, heightCm: nh })));
  };

  /** Shared gate for the two bulk SKU rewriters. Returns true when the rewrite may run. */
  const allowSkuRewrite = (action: "pattern" | "prefix_suffix"): boolean => {
    const guard = skuRewriteGuard({
      action,
      manualCount: manualSkuKeys.size,
      unlocked: skuUnlocked,
      hasExistingMatrix: combinations.length > 0,
      confirmed: false,
    });
    if (guard.allowed) { setError(""); return true; }
    if (guard.needsUnlock) { setError(guard.message ?? ""); return false; }
    const confirmed = window.confirm(guard.message ?? "");
    if (confirmed) setError("");
    return confirmed;
  };

  const applyPrefixSuffix = () => {
    if (!allowSkuRewrite("prefix_suffix")) return;
    setCombinations((cs) => cs.map((c) => ({ ...c, sku: `${prefix}${c.sku}${suffix}`.replace(/[^A-Za-z0-9\-]/g, "-").replace(/-+/g, "-") })));
    setManualSkuKeys(new Set());
    setSkuUnlocked(false);
  };

  const applyPatternToAll = () => {
    const patternErr = validateSkuPattern(skuPattern, parsedOptions.map((o) => o.name));
    if (patternErr) { setError(patternErr); return; }
    if (!allowSkuRewrite("pattern")) return;
    const used = new Set<string>();
    setCombinations((cs) => cs.map((c, idx) => {
      const codes: Record<string, string> = {};
      for (let i = 0; i < parsedOptions.length && i < c.values.length; i++) {
        codes[parsedOptions[i].name] = canonicalOptionSkuCode(c.values[i]);
      }
      const sku = uniqueSku(generateSkuFromPattern(skuPattern, { productSku, productName, optionCodes: codes }, idx), used);
      return { ...c, sku };
    }));
    setManualSkuKeys(new Set());
    setSkuUnlocked(false);
    setError("");
  };

  const filteredCombinations = useMemo(() => {
    if (!search.trim()) return combinations;
    const q = search.toLowerCase();
    return combinations.filter((c) => c.sku.toLowerCase().includes(q) || c.values.some((v) => v.toLowerCase().includes(q)));
  }, [combinations, search]);

  const PAGE_SIZE = 50;
  const totalPages = Math.max(1, Math.ceil(filteredCombinations.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pagedCombinations = filteredCombinations.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  const activeIndex = (c: CombinationRow) => combinations.indexOf(c);

  return (
    <div className="space-y-4">
      {error && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}

      {/* Option builder */}
      <div className="space-y-3">
        <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3 text-xs text-emerald-700">
          <i className="ri-information-line mr-1" />
          Add option dimensions like Colour, Size, or Pack. Every combination of values becomes one sellable SKU.
        </div>
        {options.map((o, i) => (
          <div key={o.id} className="grid grid-cols-1 sm:grid-cols-[160px_1fr_32px] gap-2 items-start">
            <div>
              <label className={labelCls}>Option {i + 1}</label>
              <input value={o.name} onChange={(e) => updateOptionName(o.id, e.target.value)} className={inputCls} placeholder="e.g. Colour" />
            </div>
            <div>
              <label className={labelCls}>Values (comma separated)</label>
              <input
                value={draftValue(o)}
                onChange={(e) => setDrafts((d) => ({ ...d, [o.id]: e.target.value }))}
                onBlur={(e) => commitOptionValues(o.id, e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitOptionValues(o.id, e.currentTarget.value); } }}
                className={inputCls}
                placeholder="Black, White, Navy, Red"
              />
            </div>
            <div className="pt-5">
              <button type="button" onClick={() => removeOption(o.id)} className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-500" aria-label="Remove option">
                <i className="ri-close-line" />
              </button>
            </div>
          </div>
        ))}
        <button type="button" onClick={addOption} className="text-xs font-medium text-emerald-600 hover:text-emerald-700 flex items-center gap-1 whitespace-nowrap">
          <i className="ri-add-line" /> Add another option dimension
        </button>
      </div>

      {/* SKU pattern + defaults */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-slate-100">
        <div>
          <label className={labelCls}>SKU pattern</label>
          <input value={skuPattern} onChange={(e) => setSkuPattern(e.target.value)} className={inputCls} placeholder="{SKU}-{COLOUR}-{SIZE}" />
          <p className="text-xs text-slate-400 mt-1">
            Tokens: {"{SKU}"}, {"{NAME}"}, {"{INDEX}"} or option names. Preview: {generateSkuFromPattern(skuPattern, { productSku, productName, optionCodes: { Colour: "BLK", Size: "M" } }, 0)}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Cost (default)</label>
            <input type="number" step="0.01" min="0" value={sharedCost} onChange={(e) => setSharedCost(e.target.value)} className={inputCls} placeholder={defaultCost != null ? `Inherited ${money(defaultCost)}` : undefined} />
            <p className="text-[11px] text-slate-400 mt-1">Leave blank to inherit the product cost.</p>
          </div>
          <div><label className={labelCls}>Price (default)</label><input type="number" step="0.01" min="0" value={sharedSelling} onChange={(e) => setSharedSelling(e.target.value)} className={inputCls} placeholder="0.00" /></div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={buildPreview}
          disabled={parsedOptions.length === 0 || !safety.safe}
          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2"
        >
          <i className="ri-grid-line" /> Generate {comboCount > 0 ? `${comboCount.toLocaleString()} combinations` : "combinations"}
        </button>
        {comboCount > 0 && safety.warning && (
          <span className="text-amber-600 text-xs"><i className="ri-alert-line mr-1" />{safety.warning}</span>
        )}
      </div>

      {/* SKU authorship lock */}
      {combinations.length > 0 && (
        <div className="flex items-center justify-between gap-3 flex-wrap px-3 py-2.5 bg-amber-50 border border-amber-100 rounded-lg">
          <div className="flex items-start gap-2 text-xs text-amber-800">
            <span className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
              <i className={manualSkuKeys.size > 0 ? "ri-lock-line" : "ri-lock-unlock-line"} />
            </span>
            <span>
              {skuUnlocked && manualSkuKeys.size > 0
                ? "SKU editing unlocked. The next regenerate or pattern apply replaces every SKU, including manual ones."
                : skuLockSummary(manualSkuKeys.size, combinations.length).label}
            </span>
          </div>
          {manualSkuKeys.size > 0 && (
            <button
              type="button"
              onClick={() => { setSkuUnlocked((v) => !v); setError(""); }}
              className="px-3 py-1.5 text-xs font-medium text-amber-800 bg-white border border-amber-200 rounded-lg hover:bg-amber-100 whitespace-nowrap"
            >
              {skuUnlocked ? "Re-lock SKUs" : "Unlock SKUs"}
            </button>
          )}
        </div>
      )}

      {/* Matrix */}
      {generated && (
        <div className="space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-sm font-semibold text-slate-700">
              {includedCount.toLocaleString()} of {combinations.length.toLocaleString()} SKUs selected
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} placeholder="Search SKU or value..." className="w-40 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-sm" />
              <button onClick={bulkActivate} className="px-2 py-1.5 text-xs font-medium text-emerald-600 hover:bg-emerald-50 rounded-lg whitespace-nowrap">Select all filtered</button>
              <button onClick={bulkDeactivate} className="px-2 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 rounded-lg whitespace-nowrap">Deselect filtered</button>
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
            <p className="text-xs font-semibold text-slate-600">Bulk edit</p>
            <div className="flex flex-wrap gap-2 items-center">
              <input type="number" step="0.01" placeholder="Set all prices" className="w-28 px-2 py-1.5 bg-white border border-slate-200 rounded text-sm" onChange={(e) => bulkSetPrice(e.target.value)} />
              <input type="number" step="0.01" placeholder="Set all costs" className="w-28 px-2 py-1.5 bg-white border border-slate-200 rounded text-sm" onChange={(e) => bulkSetCost(e.target.value)} />
              <input type="number" step="0.01" placeholder="Weight kg" className="w-24 px-2 py-1.5 bg-white border border-slate-200 rounded text-sm" onChange={(e) => bulkSetWeight(e.target.value)} />
              <div className="flex items-center gap-1">
                <input value={bulkDims.l} onChange={(e) => setBulkDims((d) => ({ ...d, l: e.target.value }))} placeholder="L cm" className="w-16 px-2 py-1.5 bg-white border border-slate-200 rounded text-sm" />
                <input value={bulkDims.w} onChange={(e) => setBulkDims((d) => ({ ...d, w: e.target.value }))} placeholder="W cm" className="w-16 px-2 py-1.5 bg-white border border-slate-200 rounded text-sm" />
                <input value={bulkDims.h} onChange={(e) => setBulkDims((d) => ({ ...d, h: e.target.value }))} placeholder="H cm" className="w-16 px-2 py-1.5 bg-white border border-slate-200 rounded text-sm" />
                <button type="button" onClick={applyBulkDims} className="px-2 py-1.5 text-xs font-medium text-emerald-600 hover:bg-emerald-50 rounded-lg border border-emerald-200 whitespace-nowrap">Apply dims</button>
              </div>
              <select value={bulkStatus} onChange={(e) => { const v = e.target.value as "active" | "draft" | "paused"; setBulkStatus(v); bulkSetStatus(v); }} className="px-2 py-1.5 bg-white border border-slate-200 rounded text-sm text-slate-600">
                <option value="active">Set all: Active</option>
                <option value="draft">Set all: Draft</option>
                <option value="paused">Set all: Paused</option>
              </select>
              <div className="flex items-center gap-1">
                <input value={prefix} onChange={(e) => setPrefix(e.target.value)} placeholder="Prefix" className="w-20 px-2 py-1.5 bg-white border border-slate-200 rounded text-sm" />
                <input value={suffix} onChange={(e) => setSuffix(e.target.value)} placeholder="Suffix" className="w-20 px-2 py-1.5 bg-white border border-slate-200 rounded text-sm" />
                <button onClick={applyPrefixSuffix} className="px-2 py-1.5 text-xs font-medium text-emerald-600 hover:bg-emerald-50 rounded-lg border border-emerald-200 whitespace-nowrap">Apply</button>
              </div>
              <button type="button" onClick={applyPatternToAll} className="px-2 py-1.5 text-xs font-medium text-emerald-600 hover:bg-emerald-50 rounded-lg border border-emerald-200 whitespace-nowrap">Apply SKU pattern</button>
              <div className="flex items-center gap-1">
                <select value={bulkQtyMode} onChange={(e) => setBulkQtyMode(e.target.value as "set" | "add" | "sub")} className="px-2 py-1.5 bg-white border border-slate-200 rounded text-sm">
                  <option value="set">Set quantity</option>
                  <option value="add">Add</option>
                  <option value="sub">Subtract</option>
                </select>
                <input value={bulkQty} onChange={(e) => setBulkQty(e.target.value)} placeholder="Qty" className="w-16 px-2 py-1.5 bg-white border border-slate-200 rounded text-sm" />
                <button
                  type="button"
                  onClick={() => {
                    const n = Number(bulkQty);
                    if (!Number.isFinite(n)) return;
                    applyToFiltered((c) => {
                      const current = Number(c.qty ?? 0);
                      const next = bulkQtyMode === "set" ? n : bulkQtyMode === "add" ? current + n : Math.max(0, current - n);
                      return { ...c, qty: next };
                    });
                  }}
                  className="px-2 py-1.5 text-xs font-medium text-emerald-600 hover:bg-emerald-50 rounded-lg border border-emerald-200 whitespace-nowrap"
                >
                  Apply qty
                </button>
              </div>
              <button type="button" onClick={() => setShowDimOverrides((v) => !v)} className="px-2 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 rounded-lg border border-slate-200 whitespace-nowrap">
                {showDimOverrides ? "Hide variant dimension overrides" : "Different for this variant"}
              </button>
            </div>
            <p className="text-xs text-slate-400">
              {defaultLocationId
                ? "Qty is opening stock at the location chosen for this product. Use Stock by location for other warehouses."
                : "Qty is not placed as opening stock until you choose a location. Stock by location rows are unchanged."}
            </p>
          </div>

          {dupSkus.length > 0 && (
            <div className="px-3 py-2 bg-red-50 border border-red-100 rounded-lg text-xs text-red-700">
              <i className="ri-error-warning-line mr-1" />{dupSkus.length} duplicate SKU(s): {dupSkus.map((d) => d.sku).join(", ")}
            </div>
          )}

          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="max-h-96 overflow-auto">
              <table className="w-full text-sm min-w-[820px]">
                <thead className="sticky top-0 bg-slate-50 text-left text-xs text-slate-500">
                  <tr>
                    <th className="px-3 py-2 w-10"></th>
                    <th className="px-3 py-2 font-medium">Variant</th>
                    <th className="px-3 py-2 font-medium">SKU</th>
                    <th className="px-3 py-2 font-medium">Barcode</th>
                    <th className="px-3 py-2 font-medium">Cost</th>
                    <th className="px-3 py-2 font-medium">Price</th>
                    <th className="px-3 py-2 font-medium">Weight</th>
                    <th className="px-3 py-2 font-medium">Qty</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {pagedCombinations.map((c) => {
                    const idx = activeIndex(c);
                    return (
                      <tr key={idx} className={`hover:bg-slate-50 ${!c.included ? "opacity-50" : ""}`}>
                        <td className="px-3 py-2"><input type="checkbox" checked={c.included} onChange={() => toggleIncluded(idx)} className="w-4 h-4 rounded accent-emerald-600" /></td>
                        <td className="px-3 py-2 text-slate-700">{c.values.join(" / ")}</td>
                        <td className="px-3 py-2">
                          <input
                            value={c.sku}
                            onChange={(e) => {
                              setComboField(idx, "sku", e.target.value);
                              setManualSkuKeys((keys) => markManualSku(keys, c.values));
                            }}
                            className="w-36 px-2 py-1 bg-slate-50 border border-slate-200 rounded text-xs font-mono"
                          />
                        </td>
                        <td className="px-3 py-2"><input value={c.barcode ?? ""} onChange={(e) => setComboField(idx, "barcode", e.target.value === "" ? null : e.target.value)} className="w-32 px-2 py-1 bg-slate-50 border border-slate-200 rounded text-xs font-mono" placeholder="\u2014" /></td>
                        <td className="px-3 py-2"><input type="number" step="0.01" min="0" value={c.cost ?? ""} onChange={(e) => setComboField(idx, "cost", e.target.value === "" ? null : Number(e.target.value))} className="w-20 px-2 py-1 bg-slate-50 border border-slate-200 rounded text-xs" /></td>
                        <td className="px-3 py-2"><input type="number" step="0.01" min="0" value={c.price ?? ""} onChange={(e) => setComboField(idx, "price", e.target.value === "" ? null : Number(e.target.value))} className="w-20 px-2 py-1 bg-slate-50 border border-slate-200 rounded text-xs" /></td>
                        <td className="px-3 py-2"><input type="number" step="0.01" min="0" value={c.weightKg ?? ""} onChange={(e) => setComboField(idx, "weightKg", e.target.value === "" ? null : Number(e.target.value))} className="w-20 px-2 py-1 bg-slate-50 border border-slate-200 rounded text-xs" /></td>
                        <td className="px-3 py-2"><input type="number" min="0" value={c.qty ?? ""} onChange={(e) => setComboField(idx, "qty", e.target.value === "" ? null : Number(e.target.value))} className="w-16 px-2 py-1 bg-slate-50 border border-slate-200 rounded text-xs" /></td>
                        <td className="px-3 py-2"><select value={c.status} onChange={(e) => setComboField(idx, "status", e.target.value)} className="px-2 py-1 bg-slate-50 border border-slate-200 rounded text-xs text-slate-600"><option value="active">Active</option><option value="draft">Draft</option><option value="paused">Paused</option></select></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="text-xs text-slate-400">
                  {filteredCombinations.length === 0 ? "0 combinations" : `Showing ${safePage * PAGE_SIZE + 1}–${safePage * PAGE_SIZE + pagedCombinations.length} of ${filteredCombinations.length}`}
                </span>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={safePage === 0} className="px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 rounded-lg disabled:opacity-40 whitespace-nowrap">Prev</button>
                  <span className="text-xs text-slate-500">{safePage + 1} / {totalPages}</span>
                  <button type="button" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={safePage >= totalPages - 1} className="px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 rounded-lg disabled:opacity-40 whitespace-nowrap">Next</button>
                </div>
              </div>
            )}
        </div>
      )}
    </div>
  );
}
